import { Tooltip } from 'antd';
import React, { PropsWithChildren, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Loading } from 'components/atoms/loading';
import { useTranslation } from 'hooks/use-translation';
import {
  EmptyTaskList,
  RoundedCheckbox,
  TaskItem,
  TaskList,
  TaskListHeader,
} from './components';

interface Props<List> {
  title: ReactNode;
  loading: boolean;
  list: List[];
  selectAll: (selectAll: boolean) => void;
  onSelected: (item: List, selected: boolean) => void;
  empty: ReactNode;
  disabledTooltipContent: ReactNode;
  renderItem: (item: List) => JSX.Element;
}

export const TasksList = <
  List extends { selected: boolean; disabled: boolean },
>({
  title,
  loading,
  list,
  selectAll,
  onSelected,
  empty,
  disabledTooltipContent,
  renderItem,
}: PropsWithChildren<Props<List>>): JSX.Element => {
  const isAllSelected = useMemo(
    () => !loading && list.length > 0 && !list.find((item) => !item.selected),
    [list, loading],
  );
  const isAllDisabled = useMemo(
    () =>
      loading ||
      list.length === 0 ||
      list.filter((item) => item.disabled).length === list.length,
    [loading, list],
  );
  const loadingText = useTranslation('loading');

  return (
    <>
      <TaskListHeader data-cy="timesheets.tasklist.header">
        <div>
          <strong>{title}</strong>
        </div>
        <Tooltip placement="left" title={<FormattedMessage id="select_all" />}>
          <RoundedCheckbox
            checked={isAllSelected}
            disabled={isAllDisabled}
            onChange={() => selectAll(!isAllSelected)}
          />
        </Tooltip>
      </TaskListHeader>
      <TaskList
        data-cy="timesheets.tasklist"
        loading={loading}
        empty={!loading && list.length === 0}
      >
        {loading ? (
          <Loading description={loadingText} />
        ) : list.length > 0 ? (
          list.map((item) => (
            <Tooltip title={item.disabled ? disabledTooltipContent : null}>
              <TaskItem
                selected={item.selected}
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled) {
                    onSelected(item, !item.selected);
                  }
                }}
              >
                <div data-cy="timesheet.tasks">{renderItem(item)}</div>
                <RoundedCheckbox
                  checked={item.selected}
                  disabled={item.disabled}
                />
              </TaskItem>
            </Tooltip>
          ))
        ) : (
          <EmptyTaskList>{empty}</EmptyTaskList>
        )}
      </TaskList>
    </>
  );
};
